import {
    AfterViewInit,
    ApplicationRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EmbeddedViewRef,
    HostBinding,
    inject,
    OnDestroy,
    OnInit,
    Renderer2,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { QCChecklistDialogConfig, QCChecklistDialogResult } from './model';
import { QCChecklistComponent } from '../../qc-checklist/qc-checklist.component';
import { TreatmentDesignQCChecklistItem, TreatmentDesignQCChecklistItemEditModel } from '../../qc-checklist/model';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogPositionSize, TreatmentDesign, TreatmentDesignRevisionJudgementEditModel } from '../../../core/model';
import { TreatmentReviewStatus } from '../../../core/model/treatment-review.model';
import { TreatmentClient } from '../../../core/client';
import { LocalPreferenceService } from '../../../core/service/local-preference.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

export const QC_CHECKLIST_DIALOG_KEY = 'qcChecklistDialog';
import { logger } from '../../../core/utils/logger';
import { stripHtmlToPlainText } from '../../../core/utils';
import { QCRejectReasonDialogService, QCRejectReasonDialogResult } from '../qc-reject-reason-dialog';
import { QCAcceptConfirmDialogService, QCAcceptConfirmDialogResult } from '../qc-accept-confirm-dialog';
import { TranslateValuePipe } from '../../../core/pipe/translate-value.pipe';
import { SplitAndTranslatePipe } from '../../../core/pipe/split-and-translate.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { TreatmentDesignStore } from 'src/app/pages/treatment/treatment-detail-page/treatment-design.store';

@Component({
    selector: 'app-qc-checklist-dialog',
    standalone: true,
    imports: [CommonModule, ButtonModule, QCChecklistComponent, FormsModule, ConfirmPopupModule, CheckboxModule],
    providers: [
        ConfirmationService,
        DialogService,
        QCRejectReasonDialogService,
        QCAcceptConfirmDialogService,
        TranslatePipe,
        TranslateValuePipe,
        SplitAndTranslatePipe,
    ],
    templateUrl: './qc-checklist-dialog.component.html',
    styleUrls: ['./qc-checklist-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QCChecklistDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(QCChecklistComponent) checklistComponent?: QCChecklistComponent;
    @ViewChild('toggleButtonTemplate', { read: TemplateRef }) toggleButtonTemplate!: TemplateRef<void>;

    @HostBinding('class.collapsed')
    isCollapsed = false;

    checklistItems: TreatmentDesignQCChecklistItem[] = [];
    allConfirmed = false;
    isAccepting = false;
    isRejecting = false;
    isSavingChecklist = false;

    // Button disable state properties (NOT getters - per Angular rules)
    isFilesDelivered = false;
    isAlreadyRejected = false;
    isAlreadyAccepted = false;
    isRejectButtonDisabled = false;
    isAcceptButtonDisabled = true;

    protected readonly ref = inject(DynamicDialogRef, { optional: true });
    protected readonly config: QCChecklistDialogConfig;
    private readonly _unsub$ = new Subject<void>();
    private readonly _dialogConfig = inject<DynamicDialogConfig<QCChecklistDialogConfig>>(DynamicDialogConfig);
    private readonly _confirmationService = inject(ConfirmationService);
    private readonly _messageService = inject(MessageService);
    private readonly _treatmentClient = inject(TreatmentClient);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _renderer = inject(Renderer2);
    private readonly _elementRef = inject(ElementRef);
    private readonly _rejectReasonDialogService = inject(QCRejectReasonDialogService);
    private readonly _acceptConfirmDialogService = inject(QCAcceptConfirmDialogService);
    private readonly _appRef = inject(ApplicationRef);
    private readonly _designStore = inject(TreatmentDesignStore);
    private readonly _translateValuePipe = inject(TranslateValuePipe);
    private readonly _splitAndTranslatePipe = inject(SplitAndTranslatePipe);
    private readonly _localPreferenceService = inject(LocalPreferenceService);

    private _toggleButtonViewRef?: EmbeddedViewRef<void> | null;
    private _rejectReason = '';
    private _dialogElement: HTMLElement | null = null;

    constructor() {
        this.config = this._dialogConfig.data!;
        this.checklistItems = [...(this.config.checklistItems ?? [])];
    }

    /**
     * Initialize button states that don't change (from config.design).
     */
    private initializeButtonStates(design: TreatmentDesign): void {
        this.isFilesDelivered = design?.filesDelivered === true;
        this.isAlreadyRejected = design?.review?.status === TreatmentReviewStatus.rejected;
        this.isAlreadyAccepted = design?.review?.status === TreatmentReviewStatus.accepted;
        this.updateButtonDisabledStates();
    }

    /**
     * Update button disabled states based on current component state.
     * Call this whenever isAccepting, isRejecting, or allConfirmed changes.
     */
    private updateButtonDisabledStates(): void {
        this.isRejectButtonDisabled =
            this.isAccepting || this.isRejecting || this.isFilesDelivered || this.isAlreadyRejected;
        this.isAcceptButtonDisabled =
            this.isAccepting ||
            this.isRejecting ||
            !this.allConfirmed ||
            this.isFilesDelivered ||
            this.isAlreadyAccepted;
    }

    ngOnInit(): void {
        this.loadSavedChecklist();
        this.subscribeDesigns();
    }

    private subscribeDesigns() {
        this._designStore.designs$.pipe(takeUntil(this._unsub$)).subscribe(designs => {
            const design = designs.find(d => d.id === this.config.design?.id);
            this.initializeButtonStates(design!);
        });
    }

    ngAfterViewInit(): void {
        this._dialogElement = this._elementRef.nativeElement.closest('.p-dialog');
        this.injectToggleButtonIntoHeader();
    }

    ngOnDestroy(): void {
        this.saveDialogPositionSize();

        if (this._toggleButtonViewRef) {
            this._appRef.detachView(this._toggleButtonViewRef);
            this._toggleButtonViewRef.destroy();
        }
        this._unsub$.next();
        this._unsub$.complete();
    }

    /**
     * Saves the current dialog position and size to local storage.
     * Only saves when the dialog is not in collapsed state.
     */
    private saveDialogPositionSize(): void {
        if (!this._dialogElement || this.isCollapsed) {
            return;
        }

        const style = this._dialogElement.style;
        const positionSize: DialogPositionSize = {
            left: style.left || null,
            top: style.top || null,
            width: style.width || null,
            height: style.height || null,
        };

        // Only save if we have meaningful values
        if (positionSize.left || positionSize.top || positionSize.width || positionSize.height) {
            this._localPreferenceService.storeDialogPositionSize(QC_CHECKLIST_DIALOG_KEY, positionSize);
        }
    }

    onAllConfirmedChange(allConfirmed: boolean): void {
        this.allConfirmed = allConfirmed;
        this.updateButtonDisabledStates();
        this._cdr.markForCheck();
    }

    onItemsChange(items: TreatmentDesignQCChecklistItem[]): void {
        this.checklistItems = items;
        this._cdr.markForCheck();
    }

    onToggleAll(checked: boolean): void {
        this.checklistComponent?.toggleAll(checked);
    }

    onToggleCollapse(): void {
        this.isCollapsed = !this.isCollapsed;
        this.updateDialogHeight();
        this._toggleButtonViewRef?.detectChanges();
        this._cdr.markForCheck();
    }

    private updateDialogHeight(): void {
        const dialog = this._elementRef.nativeElement.closest('.p-dialog');
        if (!dialog) {
            return;
        }

        if (this.isCollapsed) {
            this._renderer.setStyle(dialog, 'height', 'auto');
        } else {
            this._renderer.setStyle(dialog, 'height', '80vh');
        }
    }

    private injectToggleButtonIntoHeader(): void {
        const header = this._elementRef.nativeElement.closest('.p-dialog')?.querySelector('.p-dialog-header');
        if (!header) {
            return;
        }

        // Create embedded view from template
        this._toggleButtonViewRef = this.toggleButtonTemplate.createEmbeddedView(undefined as void);

        // Attach to ApplicationRef to include in change detection
        this._appRef.attachView(this._toggleButtonViewRef);

        this._toggleButtonViewRef.detectChanges();

        // Append the button element to header
        const buttonElement = this._toggleButtonViewRef.rootNodes[0];
        this._renderer.appendChild(header, buttonElement);
    }

    onAcceptClick(event: Event): void {
        const { designRevisionJudgement, treatmentId, treatmentTypeId } = this.config;

        if (!designRevisionJudgement) {
            this._confirmationService.confirm({
                target: event.target as EventTarget,
                message: '确定要通过质检吗?',
                acceptButtonStyleClass: 'p-button-success p-button-text',
                rejectButtonStyleClass: 'p-button-secondary p-button-text',
                icon: 'pi pi-exclamation-triangle',
                key: 'confirm-accept-qc',
                accept: () => this.doAccept(),
            });
        } else {
            const dialogRef = this._acceptConfirmDialogService.open({
                treatmentId,
                treatmentTypeId,
                designRevisionJudgement,
            });
            dialogRef.onClose.subscribe((result: QCAcceptConfirmDialogResult | undefined) => {
                if (result?.confirmed) {
                    this.doAccept(result.revisionJudgement);
                }
            });
        }
    }

    onRejectClick(): void {
        // Pre-fill rejection reason with unchecked items (with translations applied)
        const uncheckedItems = this.checklistComponent?.getUnconfirmedItems() ?? [];
        let initialReason = '';
        if (uncheckedItems.length > 0) {
            const uncheckedList = uncheckedItems
                .map(item => {
                    const title = this.translateItemTitle(item);
                    const value = stripHtmlToPlainText(this.translateItemValue(item));
                    return `<li><strong>${title}</strong>: ${value}</li>`;
                })
                .join('\n');
            initialReason = `<p>以下质检项未通过:</p>\n<ul>\n${uncheckedList}\n</ul>`;
        }

        const dialogRef = this._rejectReasonDialogService.open({ initialReason });
        dialogRef.onClose.subscribe((result: QCRejectReasonDialogResult | undefined) => {
            if (result?.confirmed && result.reason) {
                this._rejectReason = result.reason;
                this.doReject();
            }
        });
    }

    onCancel(): void {
        this.ref?.close({
            action: 'cancel',
            checklist: this.checklistItems,
        } as QCChecklistDialogResult);
    }

    private async doAccept(revisionJudgement?: TreatmentDesignRevisionJudgementEditModel | null): Promise<void> {
        this.isAccepting = true;
        this.updateButtonDisabledStates();
        this._cdr.markForCheck();

        try {
            await this.saveChecklist();

            const result: QCChecklistDialogResult = {
                action: 'accept',
                checklist: this.checklistItems,
                revisionJudgement: revisionJudgement,
            };

            this.ref?.close(result);
        } catch (error) {
            logger.logError(`[QCChecklistDialog] Failed to save checklist on accept: ${error}`);
            this._messageService.add({
                severity: 'error',
                summary: '保存失败',
                detail: '保存质检清单失败，请重试',
            });
        } finally {
            this.isAccepting = false;
            this.isAlreadyAccepted = true;
        }
    }

    private async doReject(): Promise<void> {
        this.isRejecting = true;
        this.updateButtonDisabledStates();
        this._cdr.markForCheck();

        try {
            await this.saveChecklist();

            const result: QCChecklistDialogResult = {
                action: 'reject',
                checklist: this.checklistItems,
                rejectReason: this._rejectReason,
            };

            this.ref?.close(result);
        } catch (error) {
            logger.logError(`[QCChecklistDialog] Failed to save checklist on reject: ${error}`);
            this._messageService.add({
                severity: 'error',
                summary: '保存失败',
                detail: '保存质检清单失败，请重试',
            });
        } finally {
            this.isRejecting = false;
            this.isAlreadyRejected = true;
        }
    }

    private async loadSavedChecklist(): Promise<void> {
        const { treatmentId, designId } = this.config;

        try {
            const savedChecklist = await firstValueFrom(
                this._treatmentClient.getDesignChecklist(treatmentId, designId)
            );

            if (savedChecklist?.items && savedChecklist.items.length > 0) {
                // Merge saved state with current items by key
                this.checklistItems = this.checklistItems.map(item => {
                    const savedItem = savedChecklist.items.find(s => s.key === item.key);
                    if (savedItem) {
                        return {
                            ...item,
                            templateId: savedItem.templateId ?? item.templateId ?? null,
                            isConfirmed: savedItem.isConfirmed,
                            confirmedAt: savedItem.confirmedAt ? new Date(savedItem.confirmedAt) : null,
                            confirmedBy: savedItem.confirmedBy,
                        };
                    }
                    return item;
                });

                this.allConfirmed = this.checklistItems.every(item => item.isConfirmed);
                this.updateButtonDisabledStates();
                this._cdr.markForCheck();
            }
        } catch (error) {
            logger.logWarning(`[QCChecklistDialog] Failed to load saved checklist: ${error}`);
            // Continue with default state - this is not a critical error
        }
    }

    private async saveChecklist(): Promise<void> {
        const { treatmentId, designId } = this.config;
        this.isSavingChecklist = true;
        this._cdr.markForCheck();

        try {
            // Map items to exclude translateConfig (frontend-only field)
            const itemsToSave: TreatmentDesignQCChecklistItemEditModel[] = this.checklistItems.map(
                ({ translateConfig, ...item }) => item
            );

            await firstValueFrom(
                this._treatmentClient.saveDesignChecklist(treatmentId, designId, {
                    items: itemsToSave,
                })
            );
        } finally {
            this.isSavingChecklist = false;
            this._cdr.markForCheck();
        }
    }

    /**
     * Translates the checklist item title based on its translateConfig.
     * @param item - The checklist item to translate
     * @returns The translated title or the original title if no translation is configured
     */
    private translateItemTitle(item: TreatmentDesignQCChecklistItem): string {
        if (item.translateConfig != null && item.translateConfig.titleTranslateGroups) {
            return this._translateValuePipe.transform(item.title, item.translateConfig.titleTranslateGroups);
        }
        return item.title;
    }

    /**
     * Translates the checklist item value based on its translateConfig.
     * @param item - The checklist item to translate
     * @returns The translated value or the original value if no translation is configured
     */
    private translateItemValue(item: TreatmentDesignQCChecklistItem): string {
        if (!item.value) return '-';

        if (item.translateConfig) {
            return this._splitAndTranslatePipe.transform(
                item.value,
                item.translateConfig.valueSeparator ?? ', ',
                false,
                item.translateConfig.valueTranslateGroups ?? undefined
            );
        }

        return item.value;
    }
}
